import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth0 as useAuth0Real } from '@auth0/auth0-react';
import { useAuth0 as useAuth0Mock } from '@/MockAuth0Provider';
import ORB_URL from '../orbAsset';
const useAuth0 = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true' ? useAuth0Mock : useAuth0Real;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROTOCOLS = {
  fadiman: {
    name: 'Fadiman Protocol',
    en: 'Fadiman Protocol',
    days_on: 1,
    days_off: 2,
    desc: '1 dose day · 2 integration days',
    note: 'The most studied protocol. Allows observation of effects between doses.',
  },
  stamets: {
    name: 'Stamets Stack',
    en: 'Stamets Stack',
    days_on: 4,
    days_off: 3,
    desc: '4 dose days · 3 rest days',
    note: 'Enhances neuroplasticity by combining psilocybin with niacin and Lion\'s Mane.',
  },
  custom: {
    name: 'Custom Schedule',
    en: 'Custom Schedule',
    days_on: 1,
    days_off: 1,
    desc: 'Define your own weekly pattern',
    note: 'For experienced users with guidance from a therapist or physician.',
  },
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function MicroDosingCalendar({ embedded = false }) {
  const { getAccessTokenSilently } = useAuth0();
  const [userEmail] = useState(localStorage.getItem('userEmail'));
  const [hasProtocol, setHasProtocol] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [protocol, setProtocol] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [showIntentionModal, setShowIntentionModal] = useState(false);
  const [intentionText, setIntentionText] = useState('');
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [integrationText, setIntegrationText] = useState('');
  const [loading, setLoading] = useState(true);

  const [setupForm, setSetupForm] = useState({
    protocol_type: 'fadiman',
    start_date: new Date().toISOString().split('T')[0],
    custom_schedule: [1, 0, 0, 1, 0, 0, 0],
  });

  const authHeaders = async () => {
    try {
      const token = await getAccessTokenSilently();
      return { headers: { Authorization: `Bearer ${token}` } };
    } catch {
      return {};
    }
  };

  useEffect(() => {
    checkProtocol();
  }, []);

  const checkProtocol = async () => {
    setLoading(true);
    try {
      const cfg = await authHeaders();
      const res = await axios.get(`${API}/microdosing-protocol/${userEmail}`, cfg);
      if (res.data && res.data.id) {
        setHasProtocol(true);
        setShowSetup(false);
        await loadCalendar();
      } else {
        setShowSetup(true);
      }
    } catch {
      setShowSetup(true);
    } finally {
      setLoading(false);
    }
  };

  const loadCalendar = async () => {
    try {
      const cfg = await authHeaders();
      const res = await axios.get(`${API}/microdosing-calendar/${userEmail}?days=42`, cfg);
      setProtocol(res.data.protocol);
      setCalendar(res.data.calendar);
    } catch (e) {
      console.error('Error loading calendar:', e);
    }
  };

  const createProtocol = async () => {
    const sel = PROTOCOLS[setupForm.protocol_type];
    try {
      const cfg = await authHeaders();
      await axios.post(`${API}/microdosing-protocol`, {
        user_email: userEmail,
        protocol_type: setupForm.protocol_type,
        days_on: setupForm.protocol_type === 'custom' ? 1 : sel.days_on,
        days_off: setupForm.protocol_type === 'custom' ? 1 : sel.days_off,
        start_date: setupForm.start_date,
        custom_schedule: setupForm.protocol_type === 'custom' ? setupForm.custom_schedule : null,
      }, cfg);
      toast.success('Protocolo criado com sucesso');
      setHasProtocol(true);
      setShowSetup(false);
      await loadCalendar();
    } catch {
      toast.error('Erro ao criar protocolo');
    }
  };

  const saveIntention = async () => {
    if (!intentionText.trim()) return;
    try {
      const cfg = await authHeaders();
      await axios.post(`${API}/microdosing-intention`, {
        user_email: userEmail,
        date: selectedDay?.date,
        intention: intentionText,
      }, cfg);
      toast.success('Intention saved');
      setShowIntentionModal(false);
      setIntentionText('');
    } catch {
      toast.error('Failed to save intention');
    }
  };

  const saveIntegration = async () => {
    if (!integrationText.trim()) return;
    try {
      const cfg = await authHeaders();
      await axios.post(`${API}/journal-entries`, {
        user_email: userEmail,
        content: integrationText,
        type: 'microdose_integration',
        date: selectedDay?.date,
      }, cfg);
      toast.success('Integration reflection saved to Journal');
      setShowIntegrationModal(false);
      setIntegrationText('');
    } catch {
      toast.error('Failed to save reflection');
    }
  };

  const getMonthCalendar = () => {
    if (!protocol) return [];
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startDow = firstDay.getDay() - 1;
    if (startDow === -1) startDow = 6;
    const startDate = new Date(protocol.start_date);
    const days = [];
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      const dateStr = date.toISOString().split('T')[0];
      const daysSince = Math.floor((date - startDate) / 86400000);
      let isDoseDay = false;
      if (daysSince >= 0) {
        if (protocol.protocol_type === 'custom' && protocol.custom_schedule) {
          const len = protocol.custom_schedule.length;
          isDoseDay = protocol.custom_schedule[daysSince % len] === 1;
        } else {
          const cycle = protocol.days_on + protocol.days_off;
          isDoseDay = (daysSince % cycle) < protocol.days_on;
        }
      }
      days.push({ date: dateStr, day: d, isDoseDay, beforeStart: daysSince < 0 });
    }
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    return weeks;
  };

  const todayStr = new Date().toISOString().split('T')[0];

  const wrapperBase = embedded
    ? { padding: '20px 16px 40px', display: 'flex', flexDirection: 'column', alignItems: 'center' }
    : { minHeight: '100vh', background: 'linear-gradient(160deg,#fafbfc 0%,#f0f7f6 40%,#e8f3f1 70%,#f5f8f7 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 16px 100px' };

  // ── Loading ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        minHeight: embedded ? 200 : '100vh',
        background: embedded ? 'transparent' : 'linear-gradient(160deg,#fafbfc 0%,#f0f7f6 40%,#e8f3f1 70%,#f5f8f7 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          width: 40, height: 40,
          border: '2px solid rgba(30,107,104,0.15)',
          borderTop: '2px solid #1E6B68',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Setup Protocol ────────────────────────────────────────────
  if (showSetup) {
    return (
      <div style={{
        ...wrapperBase,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}>
        {/* Header */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 40 }}>
          <img src={ORB_URL} alt="" style={{ width: 56, height: 56, marginBottom: 16, opacity: 0.85 }} />
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(1.5rem, 5vw, 2rem)',
            color: '#1E6B68', fontWeight: 400, margin: '0 0 8px', textAlign: 'center',
          }}>
            Microdosing Protocol
          </h1>
          <p style={{ color: '#7A9E9B', fontSize: '0.92rem', textAlign: 'center', maxWidth: 300, lineHeight: 1.6 }}>
            Choose the protocol that best aligns with your process and therapeutic goals.
          </p>
        </div>

        {/* Protocol cards */}
        <div style={{ width: '100%', maxWidth: 500, display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 28 }}>
          {Object.entries(PROTOCOLS).map(([key, proto]) => {
            const selected = setupForm.protocol_type === key;
            return (
              <div
                key={key}
                onClick={() => setSetupForm(f => ({ ...f, protocol_type: key }))}
                style={{
                  padding: '20px 22px',
                  background: selected ? 'rgba(30,107,104,0.07)' : 'rgba(255,255,255,0.85)',
                  border: `2px solid ${selected ? '#1E6B68' : 'rgba(184,211,207,0.5)'}`,
                  borderRadius: 18,
                  cursor: 'pointer',
                  transition: 'all 0.22s ease',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ margin: '0 0 4px', color: '#1E6B68', fontWeight: 700, fontSize: '0.97rem' }}>
                      {proto.name}
                    </p>
                    <p style={{ margin: '0 0 8px', color: '#2D8B87', fontSize: '0.83rem', fontWeight: 500 }}>
                      {proto.desc}
                    </p>
                    <p style={{ margin: 0, color: '#8AABA8', fontSize: '0.78rem', lineHeight: 1.5, maxWidth: 320 }}>
                      {proto.note}
                    </p>
                  </div>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${selected ? '#1E6B68' : 'rgba(184,211,207,0.8)'}`,
                    background: selected ? '#1E6B68' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginLeft: 12, marginTop: 2,
                    transition: 'all 0.2s',
                  }}>
                    {selected && <span style={{ color: '#fff', fontSize: '0.7rem' }}>✓</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Custom schedule editor */}
        {setupForm.protocol_type === 'custom' && (
          <div style={{
            width: '100%', maxWidth: 500,
            background: 'rgba(255,255,255,0.85)',
            border: '1.5px solid rgba(184,211,207,0.5)',
            borderRadius: 18, padding: '20px 22px',
            marginBottom: 20,
            backdropFilter: 'blur(8px)',
          }}>
            <p style={{ margin: '0 0 14px', color: '#1E6B68', fontWeight: 600, fontSize: '0.9rem' }}>
              Design your week
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              {DAY_LABELS.map((day, idx) => {
                const on = setupForm.custom_schedule[idx] === 1;
                return (
                  <div
                    key={idx}
                    onClick={() => {
                      const s = [...setupForm.custom_schedule];
                      s[idx] = on ? 0 : 1;
                      setSetupForm(f => ({ ...f, custom_schedule: s }));
                    }}
                    style={{
                      flex: 1,
                      padding: '10px 4px',
                      background: on ? '#1E6B68' : 'rgba(184,211,207,0.25)',
                      border: `1.5px solid ${on ? '#1E6B68' : 'rgba(184,211,207,0.6)'}`,
                      borderRadius: 10,
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s',
                    }}
                  >
                    <p style={{ margin: 0, fontSize: '0.68rem', color: on ? '#fff' : '#8AABA8', fontWeight: 600 }}>{day}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '0.62rem', color: on ? 'rgba(255,255,255,0.8)' : '#8AABA8' }}>
                      {on ? 'ON' : 'off'}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Start date */}
        <div style={{ width: '100%', maxWidth: 500, marginBottom: 24 }}>
          <p style={{ color: '#1E6B68', fontWeight: 600, fontSize: '0.88rem', margin: '0 0 8px' }}>
            Start date
          </p>
          <input
            type="date"
            value={setupForm.start_date}
            onChange={e => setSetupForm(f => ({ ...f, start_date: e.target.value }))}
            style={{
              width: '100%',
              padding: '13px 16px',
              border: '1.5px solid rgba(184,211,207,0.7)',
              borderRadius: 14,
              fontSize: '0.95rem',
              color: '#1E6B68',
              background: 'rgba(255,255,255,0.9)',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Educational note */}
        <div style={{
          width: '100%', maxWidth: 500,
          background: 'rgba(30,107,104,0.05)',
          border: '1px solid rgba(30,107,104,0.12)',
          borderRadius: 12, padding: '14px 18px',
          marginBottom: 28,
        }}>
          <p style={{ margin: 0, color: '#5A8A87', fontSize: '0.78rem', lineHeight: 1.6 }}>
            <strong style={{ color: '#1E6B68' }}>Clinical note:</strong> HYPSEYA offers scheduling and integration tools for educational and tracking purposes. Always consult a healthcare professional and comply with local legislation applicable to your practice.
          </p>
        </div>

        <button
          onClick={createProtocol}
          style={{
            width: '100%', maxWidth: 500,
            padding: '16px',
            background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
            color: '#fff',
            border: 'none', borderRadius: 28,
            fontSize: '1rem', fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(30,107,104,0.3)',
          }}
        >
          Create protocol
        </button>
      </div>
    );
  }

  // ── Calendar View ─────────────────────────────────────────────
  const weeks = getMonthCalendar();

  // Today's status
  const todayCalEntry = calendar.find(c => c.date === todayStr);
  const todayIsDose = todayCalEntry?.is_dosing_day ?? false;

  return (
    <div style={{
      ...wrapperBase,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <img src={ORB_URL} alt="" style={{ width: 44, height: 44, marginBottom: 12, opacity: 0.8 }} />
        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 'clamp(1.4rem, 5vw, 1.8rem)',
          color: '#1E6B68', fontWeight: 400, margin: '0 0 6px',
        }}>
          Microdosing Calendar
        </h1>
        <p style={{ color: '#7A9E9B', fontSize: '0.88rem', margin: 0 }}>
          Your conscious practice, day by day.
        </p>
      </div>

      {/* Today's card */}
      <div style={{
        width: '100%', maxWidth: 500,
        background: todayIsDose
          ? 'linear-gradient(135deg, rgba(30,107,104,0.12) 0%, rgba(45,139,135,0.08) 100%)'
          : 'rgba(255,255,255,0.85)',
        border: `2px solid ${todayIsDose ? 'rgba(30,107,104,0.35)' : 'rgba(184,211,207,0.45)'}`,
        borderRadius: 20, padding: '20px 22px',
        marginBottom: 20,
        backdropFilter: 'blur(10px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <p style={{ margin: 0, color: '#7A9E9B', fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Today
            </p>
            <p style={{
              margin: '4px 0 0',
              color: '#1E6B68',
              fontFamily: "'Playfair Display', serif",
              fontSize: '1.05rem',
            }}>
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
          <div style={{
            padding: '6px 14px',
            borderRadius: 20,
            background: todayIsDose ? '#1E6B68' : 'rgba(184,211,207,0.4)',
            color: todayIsDose ? '#fff' : '#7A9E9B',
            fontSize: '0.78rem', fontWeight: 600,
          }}>
            {todayIsDose ? '✦ Dose day' : '○ Integration'}
          </div>
        </div>
        <p style={{ margin: '0 0 14px', color: '#5A8A87', fontSize: '0.84rem', lineHeight: 1.55 }}>
          {todayIsDose
            ? 'Set your intention before the dose. What do you want to explore or work with today?'
            : 'Rest and integration day. What has emerged in recent days? Record your observations.'}
        </p>
        <button
          onClick={() => {
            setSelectedDay({ date: todayStr, isDoseDay: todayIsDose });
            todayIsDose ? setShowIntentionModal(true) : setShowIntegrationModal(true);
          }}
          style={{
            padding: '11px 22px',
            background: todayIsDose
              ? 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)'
              : 'rgba(30,107,104,0.08)',
            color: todayIsDose ? '#fff' : '#1E6B68',
            border: todayIsDose ? 'none' : '1.5px solid rgba(30,107,104,0.25)',
            borderRadius: 22,
            fontSize: '0.88rem', fontWeight: 600,
            cursor: 'pointer',
            boxShadow: todayIsDose ? '0 4px 16px rgba(30,107,104,0.25)' : 'none',
          }}
        >
          {todayIsDose ? '✦ Set intention' : '◎ Integration reflection'}
        </button>
      </div>

      {/* Protocol info + change */}
      {protocol && (
        <div style={{
          width: '100%', maxWidth: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'rgba(255,255,255,0.7)',
          border: '1px solid rgba(184,211,207,0.4)',
          borderRadius: 14, padding: '12px 18px',
          marginBottom: 20,
        }}>
          <div>
            <p style={{ margin: 0, color: '#1E6B68', fontWeight: 600, fontSize: '0.85rem' }}>
              {PROTOCOLS[protocol.protocol_type]?.name || 'Custom protocol'}
            </p>
            <p style={{ margin: '2px 0 0', color: '#9ABAAA', fontSize: '0.75rem' }}>
              Start: {new Date(protocol.start_date).toLocaleDateString('en-GB')}
            </p>
          </div>
          <button
            onClick={() => { setHasProtocol(false); setShowSetup(true); }}
            style={{
              background: 'transparent',
              border: '1px solid rgba(30,107,104,0.25)',
              color: '#1E6B68',
              padding: '6px 14px',
              borderRadius: 20,
              fontSize: '0.78rem',
              cursor: 'pointer',
            }}
          >
            Change
          </button>
        </div>
      )}

      {/* Calendar */}
      <div style={{
        width: '100%', maxWidth: 500,
        background: 'rgba(255,255,255,0.88)',
        border: '1.5px solid rgba(184,211,207,0.45)',
        borderRadius: 22,
        overflow: 'hidden',
        backdropFilter: 'blur(10px)',
        marginBottom: 20,
      }}>
        {/* Month nav */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 22px',
          borderBottom: '1px solid rgba(184,211,207,0.3)',
        }}>
          <button
            onClick={() => { const m = new Date(currentMonth); m.setMonth(m.getMonth() - 1); setCurrentMonth(m); }}
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(30,107,104,0.08)',
              border: 'none', color: '#1E6B68', fontSize: '1.1rem',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >‹</button>
          <p style={{
            margin: 0,
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.05rem', color: '#1E6B68', fontWeight: 400,
          }}>
            {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </p>
          <button
            onClick={() => { const m = new Date(currentMonth); m.setMonth(m.getMonth() + 1); setCurrentMonth(m); }}
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(30,107,104,0.08)',
              border: 'none', color: '#1E6B68', fontSize: '1.1rem',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >›</button>
        </div>

        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', padding: '10px 12px 0' }}>
          {DAY_LABELS.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: '0.68rem', color: '#AABAB8', fontWeight: 600, paddingBottom: 6, letterSpacing: '0.04em' }}>
              {d}
            </div>
          ))}
        </div>

        {/* Grid */}
        <div style={{ padding: '0 12px 16px' }}>
          {weeks.map((week, wi) => (
            <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px', marginBottom: 4 }}>
              {week.map((day, di) => {
                if (!day) return <div key={di} />;
                const isToday = day.date === todayStr;
                const isSelected = selectedDay?.date === day.date;
                return (
                  <div
                    key={di}
                    onClick={() => !day.beforeStart && setSelectedDay(day)}
                    style={{
                      aspectRatio: '1',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      cursor: day.beforeStart ? 'default' : 'pointer',
                      background: isSelected
                        ? (day.isDoseDay ? '#1E6B68' : 'rgba(30,107,104,0.18)')
                        : isToday
                          ? (day.isDoseDay ? 'rgba(30,107,104,0.2)' : 'rgba(184,211,207,0.25)')
                          : day.isDoseDay
                            ? 'rgba(30,107,104,0.1)'
                            : 'transparent',
                      border: isToday
                        ? `2px solid ${day.isDoseDay ? '#1E6B68' : 'rgba(30,107,104,0.35)'}`
                        : isSelected
                          ? `2px solid ${day.isDoseDay ? '#1E6B68' : 'rgba(30,107,104,0.4)'}`
                          : '2px solid transparent',
                      transition: 'all 0.18s ease',
                      opacity: day.beforeStart ? 0.3 : 1,
                      position: 'relative',
                    }}
                  >
                    <span style={{
                      fontSize: '0.82rem',
                      fontWeight: isToday || isSelected ? 700 : (day.isDoseDay ? 600 : 400),
                      color: isSelected && day.isDoseDay ? '#fff' : (day.isDoseDay ? '#1E6B68' : '#7A9E9B'),
                    }}>
                      {day.day}
                    </span>
                    {day.isDoseDay && !isSelected && (
                      <div style={{
                        width: 4, height: 4, borderRadius: '50%',
                        background: '#1E6B68', opacity: 0.6,
                        marginTop: 1,
                      }} />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div style={{
          display: 'flex', gap: 20, justifyContent: 'center',
          padding: '12px 22px 16px',
          borderTop: '1px solid rgba(184,211,207,0.25)',
        }}>
          {[
            { color: '#1E6B68', label: 'Dose day' },
            { color: 'rgba(184,211,207,0.6)', label: 'Integration' },
          ].map(({ color, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
              <span style={{ fontSize: '0.75rem', color: '#7A9E9B' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Selected day actions */}
      {selectedDay && (
        <div style={{
          width: '100%', maxWidth: 500,
          background: 'rgba(255,255,255,0.9)',
          border: '1.5px solid rgba(184,211,207,0.5)',
          borderRadius: 18, padding: '18px 20px',
          backdropFilter: 'blur(10px)',
          animation: 'fadeUp 0.3s ease',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <p style={{
              margin: 0, color: '#1E6B68',
              fontFamily: "'Playfair Display', serif",
              fontSize: '0.98rem',
            }}>
              {new Date(selectedDay.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <button onClick={() => setSelectedDay(null)} style={{
              background: 'none', border: 'none', color: '#AABAB8',
              fontSize: '1.2rem', cursor: 'pointer', padding: '0 4px',
            }}>×</button>
          </div>
          <p style={{
            margin: '0 0 14px',
            background: selectedDay.isDoseDay ? 'rgba(30,107,104,0.12)' : 'rgba(184,211,207,0.25)',
            color: selectedDay.isDoseDay ? '#1E6B68' : '#7A9E9B',
            display: 'inline-block',
            padding: '3px 12px', borderRadius: 20,
            fontSize: '0.78rem', fontWeight: 600,
          }}>
            {selectedDay.isDoseDay ? '✦ Dose day' : '○ Integration day'}
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            {selectedDay.isDoseDay ? (
              <button
                onClick={() => setShowIntentionModal(true)}
                style={{
                  flex: 1, padding: '12px',
                  background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
                  color: '#fff', border: 'none', borderRadius: 20,
                  fontSize: '0.86rem', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Set intention
              </button>
            ) : (
              <button
                onClick={() => setShowIntegrationModal(true)}
                style={{
                  flex: 1, padding: '12px',
                  background: 'rgba(30,107,104,0.08)',
                  color: '#1E6B68',
                  border: '1.5px solid rgba(30,107,104,0.22)',
                  borderRadius: 20,
                  fontSize: '0.86rem', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Integration reflection
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Intention Modal ── */}
      {showIntentionModal && (
        <div
          onClick={() => setShowIntentionModal(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(3,12,11,0.65)',
            backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            zIndex: 2000, padding: '0 16px',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 500,
              background: '#fff',
              borderRadius: '24px 24px 0 0',
              padding: '28px 24px 40px',
              animation: 'slideUp 0.35s ease',
            }}
          >
            <div style={{ width: 40, height: 4, borderRadius: 2, background: '#e0e0e0', margin: '0 auto 22px' }} />
            <p style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '1.1rem', color: '#1E6B68', margin: '0 0 6px',
            }}>
              Today's intention
            </p>
            <p style={{ color: '#8AABA8', fontSize: '0.82rem', margin: '0 0 18px', lineHeight: 1.5 }}>
              What do you want to explore, work with, or let go of in this session?
            </p>
            <textarea
              value={intentionText}
              onChange={e => setIntentionText(e.target.value)}
              placeholder="Write your intention here..."
              style={{
                width: '100%', minHeight: 110,
                padding: '14px 16px',
                border: '1.5px solid rgba(184,211,207,0.7)',
                borderRadius: 14,
                fontSize: '0.92rem', color: '#1E6B68',
                lineHeight: 1.6, resize: 'vertical',
                fontFamily: 'inherit', outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <button
              onClick={saveIntention}
              style={{
                width: '100%', marginTop: 14,
                padding: '15px',
                background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
                color: '#fff', border: 'none', borderRadius: 24,
                fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(30,107,104,0.25)',
              }}
            >
              Save intention
            </button>
          </div>
        </div>
      )}

      {/* ── Integration Modal ── */}
      {showIntegrationModal && (
        <div
          onClick={() => setShowIntegrationModal(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(3,12,11,0.65)',
            backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            zIndex: 2000, padding: '0 16px',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 500,
              background: '#fff',
              borderRadius: '24px 24px 0 0',
              padding: '28px 24px 40px',
              animation: 'slideUp 0.35s ease',
            }}
          >
            <div style={{ width: 40, height: 4, borderRadius: 2, background: '#e0e0e0', margin: '0 auto 22px' }} />
            <p style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '1.1rem', color: '#1E6B68', margin: '0 0 6px',
            }}>
              Integration reflection
            </p>
            <p style={{ color: '#8AABA8', fontSize: '0.82rem', margin: '0 0 18px', lineHeight: 1.5 }}>
              What emerged? What did you observe in your body, emotions, or thoughts? This reflection will be saved to your Journal.
            </p>
            <textarea
              value={integrationText}
              onChange={e => setIntegrationText(e.target.value)}
              placeholder="Write your reflection here..."
              style={{
                width: '100%', minHeight: 130,
                padding: '14px 16px',
                border: '1.5px solid rgba(184,211,207,0.7)',
                borderRadius: 14,
                fontSize: '0.92rem', color: '#1E6B68',
                lineHeight: 1.6, resize: 'vertical',
                fontFamily: 'inherit', outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <button
              onClick={saveIntegration}
              style={{
                width: '100%', marginTop: 14,
                padding: '15px',
                background: 'rgba(30,107,104,0.08)',
                color: '#1E6B68',
                border: '1.5px solid rgba(30,107,104,0.25)',
                borderRadius: 24,
                fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Save to Journal
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { transform: translateY(40px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
